import {
  ChangeEmailResendOtpResult,
  ChangeEmailVerifyOtpResult,
  ChangeEmailVerifyOtpResultTypename,
  Typenames,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { OtpVerificationType } from 'features/otp/constants/OtpVerificationType';
import OtpScreen from 'features/otp/OtpScreen';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ChangeEmailResendOtp from 'services/graphql/mutations/ChangeEmailResendOtp';
import ChangeEmailVerifyOtp from 'services/graphql/mutations/ChangeEmailVerifyOtp';
import useCancellableMutation from 'shared/hooks/useCancellableMutation';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

type ChangeEmailVerifyOtpMutation = {
  changeEmailVerifyOtp?: ChangeEmailVerifyOtpResult;
};

type ChangeEmailVerifyOtpMutationVariables = {
  input: {
    otp: string;
  };
};

type ChangeEmailResendOtpMutation = {
  changeEmailResendOtp?: ChangeEmailResendOtpResult;
};

const VERIFY_SCREEN_ERROR_CODES: ChangeEmailVerifyOtpResultTypename[] = [
  Typenames.ChangeEmailTooManyOtpAttempts,
];

export const VerifyPhoneNumber = () => {
  const router = useRouter();
  const { t: translatedTextOtp } = useTranslation('otp');
  const { t: translatedTextOtpError } = useTranslation('otp', {
    keyPrefix: 'otpError',
  });
  const { lastFourPhoneDigits } = useContext(MyVaroContext);

  const [{ fetching: verifyFetching }, executeVerifyMutation] =
    useCancellableMutation<
      ChangeEmailVerifyOtpMutation,
      ChangeEmailVerifyOtpMutationVariables
    >(ChangeEmailVerifyOtp);

  const [{ fetching: resendFetching }, executeResendMutation] =
    useCancellableMutation<ChangeEmailResendOtpMutation>(ChangeEmailResendOtp);

  const handleVerifyOtp = async (code: string) => {
    try {
      const response = await executeVerifyMutation({
        input: { otp: code },
      });

      const verifyResponse = response.data?.changeEmailVerifyOtp;

      if (
        verifyResponse?.__typename === Typenames.ChangeEmailVerifyOtpSuccess
      ) {
        router.navigate('/myvaro/personal-details/change-email/confirm-email');
        return { success: true };
      }

      if (verifyResponse?.__typename === Typenames.ChangeEmailIncorrectOtp) {
        return {
          errorCode: Typenames.ChangeEmailIncorrectOtp,
          title: verifyResponse.title,
          message: verifyResponse.message,
        };
      }

      if (verifyResponse?.__typename === Typenames.ChangeEmailExpiredOtp) {
        return {
          errorCode: Typenames.ChangeEmailExpiredOtp,
          title: verifyResponse.title,
          message: verifyResponse.message,
        };
      }

      if (verifyResponse?.__typename === Typenames.ChangeEmailError) {
        return {
          errorCode: Typenames.ChangeEmailError,
          title: verifyResponse.title,
          message: verifyResponse.message,
        };
      }

      if (
        verifyResponse?.__typename === Typenames.ChangeEmailTooManyOtpAttempts
      ) {
        return {
          errorCode: Typenames.ChangeEmailTooManyOtpAttempts,
          title: verifyResponse.title,
          message: verifyResponse.message,
        };
      }

      return {
        success: false,
        message: translatedTextOtpError('tryAgainMessage'),
      };
    } catch (error) {
      logErrorWithContext('My Varo Change Email verify OTP error: ' + error, {
        featureArea: FeatureArea.AUTHENTICATION,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.HIGH,
      });
      return {
        success: false,
        message: translatedTextOtp('genericError'),
      };
    }
  };

  const handleResendOtp = async () => {
    try {
      const response = await executeResendMutation();

      const resendResponse = response.data?.changeEmailResendOtp;

      if (
        resendResponse?.__typename === Typenames.ChangeEmailResendOtpSuccess
      ) {
        return { success: true };
      }

      if (resendResponse?.__typename === Typenames.ChangeEmailTooManyAttempts) {
        return {
          errorCode: Typenames.ChangeEmailTooManyAttempts,
          title: resendResponse.title,
          message: resendResponse.message,
        };
      }

      return {
        errorCode: 500,
        message: translatedTextOtpError('codeFiledToSend'),
      };
    } catch (error) {
      logErrorWithContext('My Varo Change Email resend OTP error: ' + error, {
        featureArea: FeatureArea.AUTHENTICATION,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.HIGH,
      });

      return {
        errorCode: 500,
        message: translatedTextOtp('genericError'),
      };
    }
  };

  const description = `${translatedTextOtp('common.description')}${String(
    lastFourPhoneDigits,
  )}.`;

  return (
    <OtpScreen
      flowKey={OtpFlowKey.PROFILE_CHANGE}
      verificationType={OtpVerificationType.PHONE}
      description={description}
      isLoading={verifyFetching || resendFetching}
      screenLevelErrorCodes={VERIFY_SCREEN_ERROR_CODES}
      onVerifyOtp={handleVerifyOtp}
      onResendOtp={handleResendOtp}
      onBack={() => router.back()}
    />
  );
};

export default VerifyPhoneNumber;
